import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import useWellness from 'state/wellness';
import {
    BRAND_GREEN_PRIMARY,
    DARKER_TEXT,
    DARK_TEXT,
    ERROR_RED,
    WARNING_ORANGE,
    SUCCESS_GREEN,
} from 'resources/constants/colors';

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const Section = styled.div`
    animation: ${fadeUp} 0.35s ease both;
`;

const SectionLabel = styled.p`
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: #aaa;
    margin: 0 0 8px;
    padding: 0 2px;
`;

const Card = styled.div`
    background: #fff;
    border-radius: 16px;
    padding: 16px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.06);
`;

const AlertItem = styled(Link)`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    text-decoration: none;
    border-bottom: 1px solid #f2f2f2;

    &:last-child { border-bottom: none; }
    &:active { opacity: 0.7; }
`;

const AlertIcon = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
    background: ${({ bg }) => bg || '#f5f5f5'};
`;

const AlertInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const AlertTitle = styled.p`
    font-size: 13px;
    font-weight: 600;
    color: ${DARKER_TEXT};
    margin: 0 0 2px;
`;

const AlertSub = styled.p`
    font-size: 11px;
    color: ${DARK_TEXT};
    margin: 0;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 20px 10px;
`;

const EmptyText = styled.p`
    font-size: 14px;
    color: ${DARK_TEXT};
    margin: 4px 0 0;
`;

const Badge = styled.span`
    background: ${ERROR_RED};
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    border-radius: 12px;
    padding: 2px 8px;
    margin-left: 6px;
`;

const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffH = Math.round((now - d) / 3600000);
    if (diffH < 1) return 'Hace un momento';
    if (diffH < 24) return `Hace ${diffH}h`;
    const diffD = Math.round(diffH / 24);
    return `Hace ${diffD} día${diffD > 1 ? 's' : ''}`;
};

const AlertRadar = () => {
    const [wellness, wellnessDispatcher] = useWellness();
    const { alerts } = wellness;

    useEffect(() => {
        wellnessDispatcher.fetchAlertsStart();
    }, []);

    const hasSos = alerts.sosAlerts?.length > 0;
    const hasMood = alerts.moodAlerts?.length > 0;
    const isEmpty = !hasSos && !hasMood;

    return (
        <Section>
            <SectionLabel>
                Radar de alertas
                {alerts.total > 0 && <Badge>{alerts.total}</Badge>}
            </SectionLabel>
            <Card>
                {isEmpty && (
                    <EmptyState>
                        <span style={{ fontSize: 28 }}>☀️</span>
                        <EmptyText>Todo tranquilo por ahora</EmptyText>
                    </EmptyState>
                )}

                {hasSos && alerts.sosAlerts.map((a, i) => (
                    <AlertItem key={`sos-${i}`} to={`/home/expediente/${a.clientId}`}>
                        <AlertIcon bg={`${ERROR_RED}18`}>🆘</AlertIcon>
                        <AlertInfo>
                            <AlertTitle>
                                {a.clientName || 'Paciente'} activó botón SOS
                            </AlertTitle>
                            <AlertSub>
                                {a.actionType === 'saptel' ? 'Llamó a SAPTEL' :
                                 a.actionType === '911' ? 'Llamó al 911' :
                                 a.actionType === 'contact' ? 'Contactó a persona de confianza' :
                                 'Acción de crisis'} · {formatDate(a.date)}
                            </AlertSub>
                        </AlertInfo>
                    </AlertItem>
                ))}

                {hasMood && alerts.moodAlerts.map((a, i) => (
                    <AlertItem key={`mood-${i}`} to={`/home/expediente/${a.clientId}`}>
                        <AlertIcon bg={`${WARNING_ORANGE}18`}>📉</AlertIcon>
                        <AlertInfo>
                            <AlertTitle>
                                {a.clientName || 'Paciente'} — tendencia negativa
                            </AlertTitle>
                            <AlertSub>
                                {a.consecutiveDays} días consecutivos con mood bajo
                            </AlertSub>
                        </AlertInfo>
                    </AlertItem>
                ))}
            </Card>
        </Section>
    );
};

export default AlertRadar;
